import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Badge, Button, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import {
  Globe,
  Package,
  RefreshCw,
  Search,
  ShoppingBag,
  Truck,
} from "react-feather";
import UserLayout from "../components/custom/layouts/UserLayout";
import useOnlineOrderNotifications from "../components/custom/hooks/useOnlineOrderNotifications";
import Modal from "../components/Modal";
import { ToastTypes, useToast } from "../components/toast/ToastProvider";
import { errorHandler } from "../config/api";
import { rupeeFormat } from "../config/functions";
import {
  getOnlineOrderById,
  getOnlineOrders,
  ONLINE_ORDER_STATUS_FLOW,
  type OnlineOrderDetail,
  type OnlineOrderStatus,
  type OnlineOrderSummary,
  updateOnlineOrderStatus,
} from "../services/onlineOrder.service";
import { ProductType } from "../redux/actions/productAction";
import { StoreState } from "../redux/store";
import { lineProductLabel } from "../utils/onlineOrderLineDisplay";
import "./OnlineOrders.css";

const STATUS_LABELS: Record<OnlineOrderStatus, string> = {
  new: "New",
  confirmed: "Confirmed",
  packed: "Packed",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const ACTION_LABELS: Record<OnlineOrderStatus, string> = {
  new: "New",
  confirmed: "Confirm",
  packed: "Pack",
  shipped: "Ship",
  delivered: "Deliver",
  cancelled: "Cancel",
};

const STATUS_FILTER_OPTIONS: Array<OnlineOrderStatus | ""> = [
  "",
  "new",
  "confirmed",
  "packed",
  "shipped",
  "delivered",
  "cancelled",
];

const TIMELINE_STEPS: OnlineOrderStatus[] = [
  "new",
  "confirmed",
  "packed",
  "shipped",
  "delivered",
];

const statusVariant = (status: OnlineOrderStatus) => {
  switch (status) {
    case "new":
      return "primary";
    case "confirmed":
      return "info";
    case "packed":
      return "warning";
    case "shipped":
      return "secondary";
    case "delivered":
      return "success";
    case "cancelled":
      return "danger";
    default:
      return "light";
  }
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const OnlineOrders = () => {
  const toast = useToast();
  const { refreshUnreadOrders } = useOnlineOrderNotifications();
  const productList = useSelector<StoreState, ProductType[]>(
    (state) => state.product,
  );

  const { productByCode, productById } = useMemo(() => {
    const byCode = new Map<string, ProductType>();
    const byId = new Map<number, ProductType>();
    productList.forEach((p) => {
      if (p.productCode) byCode.set(String(p.productCode), p);
      if (p.id != null) byId.set(Number(p.id), p);
      if (p.productId != null) byId.set(Number(p.productId), p);
    });
    return { productByCode: byCode, productById: byId };
  }, [productList]);

  const [orders, setOrders] = useState<OnlineOrderSummary[]>([]);
  const [allOrders, setAllOrders] = useState<OnlineOrderSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<OnlineOrderStatus | "">("");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState<OnlineOrderDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [lastSynced, setLastSynced] = useState<Date | null>(null);

  const loadOrders = useCallback(
    async (showLoader = true) => {
      if (showLoader) {
        setLoading(true);
      }

      try {
        const [listRes, allRes] = await Promise.all([
          getOnlineOrders(statusFilter || undefined),
          getOnlineOrders(),
        ]);
        setOrders(listRes.data ?? []);
        setAllOrders(allRes.data ?? []);
        setLastSynced(new Date());
      } catch (e: unknown) {
        toast(errorHandler(e, "Failed to load orders"), ToastTypes.ERROR);
      } finally {
        if (showLoader) {
          setLoading(false);
        }
      }
    },
    [statusFilter, toast],
  );

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    const onFocus = () => void loadOrders(false);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadOrders]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { "": allOrders.length };
    STATUS_FILTER_OPTIONS.filter(Boolean).forEach((s) => {
      counts[s] = allOrders.filter((o) => o.status === s).length;
    });
    return counts;
  }, [allOrders]);

  const stats = useMemo(() => {
    const newCount = allOrders.filter((o) => o.status === "new").length;
    const activeCount = allOrders.filter(
      (o) => o.status !== "delivered" && o.status !== "cancelled",
    ).length;
    const totalValue = allOrders.reduce(
      (sum, o) => sum + Number(o.grandTotal || 0),
      0,
    );
    return { newCount, activeCount, totalValue, total: allOrders.length };
  }, [allOrders]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter(
      (o) =>
        o.orderNumber.toLowerCase().includes(q) ||
        o.customerName.toLowerCase().includes(q) ||
        o.customerPhone.includes(q) ||
        (o.invoiceNumber ?? "").toLowerCase().includes(q),
    );
  }, [orders, search]);

  const openDetail = async (id: number) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await getOnlineOrderById(id);
      setDetail(res.data ?? null);
    } catch {
      toast("Failed to load order details", ToastTypes.ERROR);
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusUpdate = async (
    order: OnlineOrderSummary,
    nextStatus: OnlineOrderStatus,
  ) => {
    if (
      nextStatus === "confirmed" &&
      !window.confirm(
        "Confirm this order? This will post the sale (stock, transactions) like POS.",
      )
    ) {
      return;
    }
    setUpdatingId(order.id);
    try {
      const res = await updateOnlineOrderStatus(order.id, nextStatus);
      toast(res.message ?? "Status updated", ToastTypes.SUCCESS);
      await loadOrders(false);
      void refreshUnreadOrders();
      if (detail?.id === order.id) {
        const refreshed = await getOnlineOrderById(order.id);
        setDetail(refreshed.data ?? null);
      }
    } catch (e: unknown) {
      const msg =
        e instanceof Error ? e.message : "Failed to update status";
      toast(msg, ToastTypes.ERROR);
    } finally {
      setUpdatingId(null);
    }
  };

  const renderActions = (order: OnlineOrderSummary, compact = false) => {
    const next = ONLINE_ORDER_STATUS_FLOW[order.status];
    if (next.length === 0) {
      return <span className="text-muted small">—</span>;
    }

    return (
      <div
        className="d-flex flex-wrap gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        {next.map((s) => (
          <Button
            key={s}
            size="sm"
            className={compact ? "online-orders-action-btn" : undefined}
            variant={s === "cancelled" ? "outline-danger" : "outline-primary"}
            disabled={updatingId === order.id}
            onClick={() => handleStatusUpdate(order, s)}
          >
            {updatingId === order.id ? (
              <Spinner size="sm" animation="border" />
            ) : (
              ACTION_LABELS[s]
            )}
          </Button>
        ))}
      </div>
    );
  };

  const renderTimeline = (status: OnlineOrderStatus) => {
    if (status === "cancelled") {
      return (
        <div className="online-orders-timeline">
          {TIMELINE_STEPS.map((step) => (
            <div
              key={step}
              className="online-orders-timeline-step is-cancelled"
            >
              {STATUS_LABELS[step]}
            </div>
          ))}
          <div className="online-orders-timeline-step is-current is-cancelled">
            Cancelled
          </div>
        </div>
      );
    }

    const currentIndex = TIMELINE_STEPS.indexOf(status);

    return (
      <div className="online-orders-timeline">
        {TIMELINE_STEPS.map((step, index) => {
          let className = "online-orders-timeline-step";
          if (index < currentIndex) className += " is-done";
          if (index === currentIndex) className += " is-current";
          return (
            <div key={step} className={className}>
              {STATUS_LABELS[step]}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <UserLayout>
      <div className="online-orders-page container-fluid py-3">
        <section className="online-orders-hero">
          <div className="online-orders-hero-top">
            <div>
              <h2>
                <Globe size={22} className="me-2 align-text-bottom" />
                Online Shop Orders
              </h2>
              <p>
                Manage web orders, confirm sales to POS, and track fulfilment
                in one place.
              </p>
            </div>
            <span className="online-orders-hero-badge">
              <ShoppingBag size={14} />
              Live from online shop
            </span>
          </div>
          <div className="online-orders-stats">
            <div className="online-orders-stat">
              <strong>{stats.newCount}</strong>
              <span>New orders</span>
            </div>
            <div className="online-orders-stat">
              <strong>{stats.activeCount}</strong>
              <span>In progress</span>
            </div>
            <div className="online-orders-stat">
              <strong>{stats.total}</strong>
              <span>All orders</span>
            </div>
            <div className="online-orders-stat">
              <strong>{rupeeFormat(stats.totalValue)}</strong>
              <span>Order value</span>
            </div>
          </div>
        </section>

        <section className="online-orders-toolbar">
          <div className="online-orders-toolbar-inner">
            <div className="online-orders-filters">
              {STATUS_FILTER_OPTIONS.map((status) => {
                const label = status === "" ? "All" : STATUS_LABELS[status];
                const count = statusCounts[status] ?? 0;
                const isActive = statusFilter === status;
                return (
                  <button
                    key={status || "all"}
                    type="button"
                    className={`online-orders-filter-chip${isActive ? " is-active" : ""}`}
                    onClick={() => setStatusFilter(status)}
                  >
                    {label}
                    <span className="chip-count">{count}</span>
                  </button>
                );
              })}
            </div>

            <div className="online-orders-controls">
              <div className="online-orders-search-wrap">
                <Search className="online-orders-search-icon" />
                <Form.Control
                  placeholder="Search order #, customer, phone, invoice…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search orders"
                />
              </div>
              {lastSynced && (
                <span className="online-orders-last-sync">
                  Updated {lastSynced.toLocaleTimeString()}
                </span>
              )}
              <button
                type="button"
                className="online-orders-btn"
                disabled={loading}
                onClick={() => void loadOrders()}
              >
                <RefreshCw size={16} className={loading ? "spin" : ""} />
                Refresh
              </button>
            </div>
          </div>
        </section>

        <section className="online-orders-card">
          {loading ? (
            <div className="online-orders-loading">
              <Spinner animation="border" role="status" />
              <p className="mt-3 mb-0">Loading online orders…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="online-orders-empty">
              <div className="online-orders-empty-icon">
                <Package size={28} />
              </div>
              <h5 className="text-secondary fw-bold">No orders found</h5>
              <p className="mb-0 small">
                {search || statusFilter
                  ? "Try changing filters or search terms."
                  : "New online shop orders will appear here automatically."}
              </p>
            </div>
          ) : (
            <div className="online-orders-table-wrap">
              <Table
                responsive
                hover
                size="sm"
                className="online-orders-table mb-0"
              >
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Order</th>
                    <th>Placed</th>
                    <th>Customer</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th>Invoice</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((order, idx) => (
                    <tr
                      key={order.id}
                      className={`order-row${order.status === "new" ? " is-new" : ""}`}
                      onClick={() => openDetail(order.id)}
                    >
                      <td className="text-muted">{idx + 1}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link online-orders-order-link p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            void openDetail(order.id);
                          }}
                        >
                          {order.orderNumber}
                        </button>
                      </td>
                      <td className="text-nowrap small">
                        {formatDateTime(order.createdAt)}
                      </td>
                      <td>
                        <div className="online-orders-customer-name">
                          {order.customerName}
                        </div>
                        <small className="text-muted">
                          {order.customerPhone}
                        </small>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border">
                          {order.itemCount}
                        </span>
                      </td>
                      <td className="fw-semibold">
                        {rupeeFormat(order.grandTotal)}
                      </td>
                      <td>{order.paymentMode}</td>
                      <td>
                        <Badge
                          bg={statusVariant(order.status)}
                          className="online-orders-status"
                        >
                          {STATUS_LABELS[order.status]}
                        </Badge>
                      </td>
                      <td className="small">
                        {order.invoiceNumber ?? "—"}
                      </td>
                      <td>{renderActions(order)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </section>
      </div>

      <Modal
        headerText={detail ? `Order ${detail.orderNumber}` : "Order details"}
        visible={detailOpen}
        onClose={() => setDetailOpen(false)}
        centered
        size="lg"
      >
        {detailLoading ? (
          <div className="online-orders-loading py-4">
            <Spinner animation="border" />
          </div>
        ) : detail ? (
          <>
            {renderTimeline(detail.status)}

            <div className="online-orders-modal-meta">
              <div className="online-orders-meta-card">
                <label>Status</label>
                <div className="value">
                  <Badge bg={statusVariant(detail.status)}>
                    {STATUS_LABELS[detail.status]}
                  </Badge>
                </div>
              </div>
              <div className="online-orders-meta-card">
                <label>Payment</label>
                <div className="value">{detail.paymentMode}</div>
              </div>
              <div className="online-orders-meta-card">
                <label>Placed on</label>
                <div className="value">{formatDateTime(detail.createdAt)}</div>
              </div>
              <div className="online-orders-meta-card">
                <label>Grand total</label>
                <div className="value text-primary fw-bold">
                  {rupeeFormat(detail.grandTotal)}
                </div>
              </div>
            </div>

            <Row className="g-2 mb-3">
              <Col md={6}>
                <div className="online-orders-meta-card h-100">
                  <label>Customer</label>
                  <div className="value">
                    {detail.customerName}
                    <br />
                    <small className="text-muted">{detail.customerPhone}</small>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="online-orders-meta-card h-100">
                  <label>
                    <Truck size={14} className="me-1" />
                    Delivery
                  </label>
                  <div className="value">{detail.deliveryDetails || "—"}</div>
                </div>
              </Col>
            </Row>

            {detail.remarks && (
              <p className="small text-muted mb-3">
                <strong>Notes:</strong> {detail.remarks}
              </p>
            )}
            {detail.invoiceNumber && (
              <p className="small mb-3">
                <strong>Sales invoice:</strong> {detail.invoiceNumber}
                {detail.salesId ? ` (ID ${detail.salesId})` : ""}
              </p>
            )}

            <Table
              size="sm"
              bordered
              className="online-orders-detail-table mb-0"
            >
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Code</th>
                  <th className="text-center">Qty</th>
                  <th className="text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(detail.salesItems ?? []).map((item, i) => {
                  const row = item as Record<string, unknown>;
                  const product = lineProductLabel(
                    row,
                    productByCode,
                    productById,
                  );
                  return (
                    <tr key={i}>
                      <td className="fw-medium">{product.name}</td>
                      <td className="text-muted small">
                        {product.code ?? "—"}
                      </td>
                      <td className="text-center">
                        {String(row.quantity ?? "—")}
                      </td>
                      <td className="text-end">
                        {rupeeFormat(Number(row.totalAmount ?? 0))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} className="text-end fw-bold">
                    Grand total
                  </td>
                  <td className="text-end fw-bold text-primary">
                    {rupeeFormat(detail.grandTotal)}
                  </td>
                </tr>
              </tfoot>
            </Table>

            <div className="online-orders-actions-bar">
              {renderActions(detail, true)}
            </div>
          </>
        ) : (
          <p className="text-muted mb-0">Order not found</p>
        )}
      </Modal>

      <style>{`
        @keyframes oo-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .online-orders-btn .spin {
          animation: oo-spin 0.8s linear infinite;
        }
      `}</style>
    </UserLayout>
  );
};

export default OnlineOrders;
